import React, { useState } from 'react';
import { adminUpdateOrder } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { CARRIERS } from './adminConstants';
import { Truck, Check, Loader2, ChevronDown } from 'lucide-react';

const physicalItems = (o) => (o.items || []).filter((it) => !it.digital && (it.qty || 0) > 0);

export const QuickTracking = ({ orders, onSaved }) => {
  const { toast } = useToast();
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});
  const [open, setOpen] = useState(false);

  const queue = orders.filter((o) =>
    ['Payment Confirmed', 'Packed'].includes(o.orderStatus) && !o.trackingNumber && physicalItems(o).length > 0);

  if (queue.length === 0) return null;

  const setD = (num, k, v) => setDrafts((p) => ({ ...p, [num]: { ...(p[num] || {}), [k]: v } }));

  const save = async (o) => {
    const d = drafts[o.orderNumber] || {};
    const tracking = (d.tracking || '').trim();
    if (!tracking) { toast({ title: 'Enter a tracking number first', variant: 'destructive' }); return; }
    setSaving((p) => ({ ...p, [o.orderNumber]: true }));
    try {
      const updated = await adminUpdateOrder(o.orderNumber, {
        carrier: d.carrier || 'Australia Post',
        trackingNumber: tracking,
        orderStatus: 'Shipped',
        notifyCustomer: true,
      });
      onSaved(updated);
      toast({ title: `${o.orderNumber} shipped`, description: 'Tracking saved and the customer has been emailed.' });
      const next = document.querySelector('[data-testid^="qt-input-"]:not([disabled])');
      if (next) next.focus();
    } catch (e) {
      toast({ title: 'Could not save tracking', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSaving((p) => ({ ...p, [o.orderNumber]: false }));
    }
  };

  return (
    <div className="border border-alp-line rounded-lg bg-alp-card/30 mb-2" data-testid="quick-tracking-panel">
      <button onClick={() => setOpen((o) => !o)} data-testid="quick-tracking-toggle"
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-alp-card/50 transition-colors rounded-lg">
        <span className="text-alp-gold text-xs tracking-[0.2em] uppercase flex items-center gap-2"><Truck size={15} /> Quick Tracking <span className="text-alp-gray normal-case tracking-normal">— {queue.length} order{queue.length === 1 ? '' : 's'} ready for a tracking number</span></span>
        <ChevronDown size={15} className={`text-alp-gray transition-transform ${open ? 'rotate-180 text-alp-gold' : ''}`} />
      </button>
      {open && (
      <div className="px-5 pb-4">
      <p className="text-alp-gray text-xs mb-3">Paste the tracking number and press Enter &mdash; the sales order is updated and marked Shipped, and the customer is emailed automatically.</p>
      <div className="divide-y divide-alp-line/50">
        {queue.map((o) => (
          <div key={o.orderNumber} className="flex flex-wrap items-center gap-2 py-2.5" data-testid={`qt-row-${o.orderNumber}`}>
            <div className="min-w-[150px] flex-1">
              <span className="block text-alp-gold text-xs tracking-[0.08em]">{o.orderNumber}</span>
              <span className="block text-alp-cream text-sm truncate">{o.customer?.fullName}</span>
            </div>
            <select
              value={(drafts[o.orderNumber] || {}).carrier || 'Australia Post'}
              onChange={(e) => setD(o.orderNumber, 'carrier', e.target.value)}
              data-testid={`qt-carrier-${o.orderNumber}`}
              className="bg-alp-dark border border-alp-line text-alp-cream text-xs rounded h-9 px-2"
            >
              {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input
              value={(drafts[o.orderNumber] || {}).tracking || ''}
              onChange={(e) => setD(o.orderNumber, 'tracking', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(o); }}
              placeholder="Tracking number"
              disabled={!!saving[o.orderNumber]}
              data-testid={`qt-input-${o.orderNumber}`}
              className="flex-1 min-w-[160px] bg-alp-dark border border-alp-line text-alp-cream text-sm rounded h-9 px-3 focus:border-alp-gold outline-none"
            />
            <button
              onClick={() => save(o)}
              disabled={!!saving[o.orderNumber]}
              data-testid={`qt-save-${o.orderNumber}`}
              className="inline-flex items-center gap-1.5 h-9 px-4 bg-alp-gold text-alp-black text-xs tracking-[0.12em] uppercase font-semibold rounded hover:bg-alp-goldlight transition-colors disabled:opacity-50"
            >
              {saving[o.orderNumber] ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Ship
            </button>
          </div>
        ))}
      </div>
      </div>
      )}
    </div>
  );
};
